import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Actions } from "../../redux/constants/actionTypes";
import UploadProgressWidget from "../../Features/Admin/Pages/Sites/Subpages/FileExplorer/Components/UploadProgressWidget";

/**
 * App-wide floating upload widget. Mounted once at the root so the minimised
 * upload progress keeps showing after the user navigates away from the File
 * Explorer (which unmounts the popup that used to own this state). All state
 * lives in redux (`sitesInfo.uploadProgress`) so it survives route changes.
 */
const GlobalUploadWidget = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const progress = useSelector((state) => state.sitesInfo?.uploadProgress);

  const { active, minimized, completed, uploaded, total, returnPath } =
    progress || {};

  // Warn on refresh/close while an upload is still running — it lives in memory
  // and a reload would abort it. Runs app-wide, since the upload continues even
  // after the user leaves the File Explorer.
  useEffect(() => {
    if (!active || completed) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [active, completed]);

  // Only the minimised state renders globally; the expanded modal is owned by
  // the File Explorer page itself.
  if (!active || !minimized) return null;

  const value = total > 0 ? (uploaded / total) * 100 : 0;

  const handleExpand = () => {
    dispatch({ type: Actions.SET_UPLOAD_PROGRESS, data: { minimized: false } });
    // Jump back to the File Explorer so the expanded popup has somewhere to
    // render, unless we're already there.
    if (returnPath && window.location.pathname !== returnPath) {
      navigate(returnPath);
    }
  };

  const handleClose = () => {
    dispatch({ type: Actions.RESET_UPLOAD_PROGRESS });
  };

  return (
    <UploadProgressWidget
      value={completed ? 100 : value}
      uploadedCount={completed ? total : Math.round((value / 100) * total)}
      total={total}
      completed={completed}
      onExpand={handleExpand}
      onClose={handleClose}
    />
  );
};

export default GlobalUploadWidget;
